import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "./StatusBadge";
import { EmptyState } from "@/components/ui/empty-state";
import type { WeeklyReport } from "@/types/WeeklyReport";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { ArrowRight, Trash2, FileText, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

interface DraftCardProps {
  report: WeeklyReport;
  onResume: (report: WeeklyReport) => void;
  onDelete: (report: WeeklyReport) => void;
  isResuming?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const DraftCard: React.FC<DraftCardProps> = ({
  report,
  onResume,
  onDelete,
  isResuming = false,
  className,
}) => {
  const formatLastEdited = (dateString?: string): string => {
    if (!dateString) return "Recently";

    try {
      const date = new Date(dateString);
      if (!isValid(date)) return "Recently";

      return `Last edited ${formatDistanceToNow(date, { addSuffix: true })}`;
    } catch {
      return "Recently";
    }
  };

  const formatDateRange = (dateRange?: string): string => {
    if (!dateRange) return "No date range";

    // If it's a date range like "Mar 24, 2023 - Mar 30, 2023"
    if (dateRange.includes(" - ")) {
      return dateRange;
    }

    // Try to parse as ISO date
    try {
      const date = new Date(dateRange);
      if (isValid(date)) {
        return format(date, "MMM d, yyyy");
      }
    } catch {
      // Fall through
    }

    return dateRange;
  };

  return (
    <Card
      className={cn(
        "transition-all duration-200 hover:shadow-md hover:shadow-primary/5",
        className,
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-semibold text-foreground truncate">
              {report.subject || "Untitled Report"}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-1">
              {formatDateRange(report.dateRange)}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <StatusBadge status={report.status || "draft"} />
            {report.clientName && (
              <Badge variant="secondary" className="text-xs">
                {report.clientName}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              {formatLastEdited(report.updatedAt || report.createdAt)}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(report)}
              className="h-8 px-3 text-xs hover:bg-destructive/10 hover:text-destructive hover:border-destructive/20"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete
            </Button>

            <Button
              onClick={() => onResume(report)}
              disabled={isResuming}
              className={cn(
                "h-8 px-3 text-xs gap-1",
                "bg-primary hover:bg-primary/90 text-primary-foreground",
                "shadow-sm hover:shadow-md transition-all duration-200",
                "hover:scale-105 active:scale-95",
              )}
            >
              {isResuming ? (
                <div className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent mr-1" />
              ) : (
                <ArrowRight className="h-3 w-3" />
              )}
              {isResuming ? "Loading..." : "Resume"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface DraftListProps {
  drafts: WeeklyReport[];
  onResume: (report: WeeklyReport) => void;
  onDelete: (report: WeeklyReport) => void;
  onCreateNew: () => void;
  isResuming?: string; // report ID being resumed
  className?: string;
}

export const DraftList: React.FC<DraftListProps> = ({
  drafts,
  onResume,
  onDelete,
  onCreateNew,
  isResuming,
  className,
}) => {
  // Sort drafts by updatedAt, then createdAt, newest first
  const sortedDrafts = [...drafts]
    .filter(
      (report) => report.status === "draft" || report.status === "pending",
    )
    .sort((a, b) => {
      const aDate = new Date(a.updatedAt || a.createdAt || 0);
      const bDate = new Date(b.updatedAt || b.createdAt || 0);
      return bDate.getTime() - aDate.getTime();
    });

  if (sortedDrafts.length === 0) {
    return (
      <div className={cn("space-y-4", className)}>
        <EmptyState
          icon={FileText}
          title="No drafts yet"
          description="Create your first report to get started with drafting."
          action={{
            label: "Create Report",
            onClick: onCreateNew,
          }}
        />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          Drafts ({sortedDrafts.length})
        </h3>
        <Button onClick={onCreateNew} size="sm" className="gap-2">
          <FileText className="h-4 w-4" />
          New Report
        </Button>
      </div>

      <div className="grid gap-4">
        {sortedDrafts.map((draft, index) => (
          <DraftCard
            key={draft.id}
            report={draft}
            onResume={onResume}
            onDelete={onDelete}
            isResuming={isResuming === draft.id}
            className="animate-slide-up"
            style={{ animationDelay: `${index * 50}ms` }}
          />
        ))}
      </div>
    </div>
  );
};

export default DraftCard;
